/**
 * Dashboard Redux Slice
 * Manages dashboard state
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { get } from '../../services/api/axiosInstance';
import { API_ENDPOINTS } from '../../services/api/apiEndpoints';

// Async thunks
export const fetchDashboardStats = createAsyncThunk(
  'dashboard/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_ENDPOINTS.DASHBOARD.STATS);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch dashboard stats'
      );
    }
  }
);

export const fetchDashboardCharts = createAsyncThunk(
  'dashboard/fetchCharts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_ENDPOINTS.DASHBOARD.CHARTS);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch dashboard charts'
      );
    }
  }
);

export const fetchRecentActivities = createAsyncThunk(
  'dashboard/fetchActivities',
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_ENDPOINTS.DASHBOARD.ACTIVITIES);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch activities'
      );
    }
  }
);

export const fetchAlerts = createAsyncThunk(
  'dashboard/fetchAlerts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_ENDPOINTS.DASHBOARD.ALERTS);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch alerts'
      );
    }
  }
);

// Initial state
const initialState = {
  stats: {
    totalMembers: 0,
    totalLoans: 0,
    totalSavings: 0,
    totalShares: 0,
    activeLoans: 0,
    pendingApplications: 0,
    monthlyDisbursements: 0,
    monthlyCollections: 0,
  },
  charts: {
    monthlyLoans: [],
    monthlyRepayments: [],
    savingsGrowth: [],
    memberGrowth: [],
  },
  activities: [],
  alerts: [],
  isLoading: false,
  error: null,
};

// Slice
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateStats: (state, action) => {
      state.stats = { ...state.stats, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    // Fetch stats
    builder
      .addCase(fetchDashboardStats.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Fetch charts
    builder
      .addCase(fetchDashboardCharts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchDashboardCharts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.charts = action.payload;
      })
      .addCase(fetchDashboardCharts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Fetch activities
    builder
      .addCase(fetchRecentActivities.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchRecentActivities.fulfilled, (state, action) => {
        state.isLoading = false;
        state.activities = action.payload;
      })
      .addCase(fetchRecentActivities.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Fetch alerts
    builder
      .addCase(fetchAlerts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAlerts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.alerts = action.payload;
      })
      .addCase(fetchAlerts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, updateStats } = dashboardSlice.actions;
export default dashboardSlice.reducer;
